import { useContext } from 'react'
import { toJS } from 'mobx'
import { isEqual } from 'lodash'
import { useQueryClient } from '@tanstack/react-query'
import { StoresContext } from '@App/contexts'
import RootStore from '@App/stores/root-store'
import useSearchQuery from '@Components/Search/SearchPanel/useSearchQuery'
import { regHistoryTabNamesAsRegistrationStatus } from '@App/helpers/reg-history-tab-names-to-source'
import RegistrationHistoryTabStore from '@App/stores/rightsholder/registrationHistory/registration-history-tab-store'
import { RegistrationHistoryTabName } from '@App/enum/registration-history-tab-name'
import { Works } from '@Entities/work/works'

export const REGISTRATION_HISTORY_QUERY_KEY = 'registration-history'

/**
 * Owns the TanStack Query wiring for one RegistrationHistoryTab. `registrationHistoryStore`'s
 * criteria/filters are shared across all four tabs, and the active rightsholder can change without
 * this session-persistent store resetting, so both must be part of the query key — and `_works`
 * must only be trusted as seed data when it was fetched under this exact combination (see
 * `RegistrationHistoryTabStore.lastResultsScope`).
 */
export const useRegistrationHistorySearch = (
    type: RegistrationHistoryTabName,
    registrationHistoryTabStore: RegistrationHistoryTabStore,
    useFilters: boolean
) => {
    const { registrationHistoryStore, rightsholderStore } = useContext<RootStore>(StoresContext)
    const queryClient = useQueryClient()

    const registrationStatusAndFilters = regHistoryTabNamesAsRegistrationStatus(type)
    const rightsholderId = rightsholderStore.currentRightsholderId
    const criteria = toJS(registrationHistoryStore.currentQuery)
    const filters = useFilters ? toJS(registrationHistoryStore.currentFilters) : undefined
    const pagination = toJS(registrationHistoryTabStore.pagination)

    const scope = registrationHistoryTabStore.lastResultsScope
    const worksMatchCurrentScope =
        !!scope &&
        scope.rightsholderId === rightsholderId &&
        isEqual(scope.query, criteria) &&
        isEqual(scope.filters, filters)

    const historyQuery = useSearchQuery<Works>({
        queryKey: [REGISTRATION_HISTORY_QUERY_KEY, rightsholderId, type, criteria, filters, pagination],
        queryFn: async () =>
            registrationHistoryTabStore.fetchRegistrationHistory(registrationStatusAndFilters, pagination, filters),
        // The layout resets this store when the page is left, while the query cache keeps the page, so the
        // results have to be copied into the store whenever the query has them, not just after a fetch.
        applyData: (response) =>
            registrationHistoryTabStore.applySearchResults(response, pagination, {
                rightsholderId,
                query: criteria,
                filters,
            }),
        initialData: worksMatchCurrentScope ? registrationHistoryTabStore.works : undefined,
        onError: (error) => registrationHistoryTabStore.reportSearchError(error),
    })

    /**
     * Call right after a mutation that's supposed to trigger a new search (Reset, or resubmitting
     * criteria/filters). If the mutation actually changed the key, the re-render already handles
     * fetching it — nothing to do here. If the "new" values happen to match what was already active
     * (e.g. Reset clicked while already at defaults, or identical criteria resubmitted after a
     * failure), the key won't change on its own, so this forces the refetch that wouldn't otherwise
     * happen. `criteria`/`filters`/`pagination` are this render's (pre-mutation) values — comparing
     * them against a fresh read of the stores after the mutation reveals whether anything moved.
     */
    const refetchIfKeyUnchanged = () => {
        const keyUnchanged =
            rightsholderId === rightsholderStore.currentRightsholderId &&
            isEqual(criteria, toJS(registrationHistoryStore.currentQuery)) &&
            isEqual(filters, useFilters ? toJS(registrationHistoryStore.currentFilters) : undefined) &&
            isEqual(pagination, toJS(registrationHistoryTabStore.pagination))
        if (keyUnchanged) {
            queryClient.invalidateQueries({ queryKey: [REGISTRATION_HISTORY_QUERY_KEY, rightsholderId, type] })
        }
    }

    return { historyQuery, refetchIfKeyUnchanged }
}

export default useRegistrationHistorySearch
